<template>
  <v-card outlined>
    <v-card-title> Filtrar </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        @submit.prevent="handleFiltrarBtnClick"
      >
        <v-row align="center">
          <v-col
            v-if="isGestor"
            cols="12"
            sm="3"
          >
            <v-select
              v-model="agenteIdSelecionado"
              :items="listaAgentes"
              item-text="nome"
              item-value="id"
              label="Agentes"
              outlined
              dense
              clearable
              hide-details
              @change="handleFiltrarBtnClick"
            />
          </v-col>

          <v-col
            cols="12"
            sm="3"
          >
            <date-picker
              :date-iso.sync="dataInicio"
              label="Data inicial"
              required
              dense
              outlined
              hide-details
            />
          </v-col>

          <v-col
            cols="12"
            sm="3"
          >
            <date-picker
              :date-iso.sync="dataFim"
              label="Data final"
              required
              dense
              outlined
              hide-details
            />
          </v-col>

          <v-col
            cols="12"
            sm="auto"
          >
            <v-btn
              color="primary"
              :loading="isGetAgendamentosLoading"
              type="submit"
            >
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <v-expand-transition>
      <div v-show="true">
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <calendario-mensal
                :key="calendarKey"
                :is-cadastro-agendamento="false"
                :events="events"
                :start-date="dataInicio"
                :end-date="dataFim"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { onMounted, ref } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import CalendarioMensal from '@/views/agenda/calendar/CalendarioMensal.vue'
import TicketService from '@/services/modules/TicketService'
import ColaboradorService from '@/services/modules/ColaboradorService' // <--- Import Service de Agentes
import useAuthStore from '@/store/auth'
import useUserStore from '@/store/user'
import { mapPeriodoDiaParaTitulo } from '@/views/agenda/calendar/functions'
import { converterLocaleDateStringParaOFormatoIsoDateString, getDataInicialMes } from '@/utils/dateTime'

export default {
  components: {
    DatePicker,
    CalendarioMensal,
  },

  setup() {
    // 1. Configuração das Stores
    const authStore = useAuthStore()
    const userStore = useUserStore()
    const { isGestor } = storeToRefs(authStore)
    const { user } = storeToRefs(userStore) // Assumindo que 'user' tem o id do logado

    const events = ref([])
    const agendamentos = ref([])
    const isGetAgendamentosLoading = ref(false)

    const calendarKey = ref(0)
    // Variáveis para o Filtro de Agentes
    const listaAgentes = ref([])
    const agenteIdSelecionado = ref(null)

    function getFinalMesSomada7Dias() {
      const date = new Date()
      const newDate = new Date(date.getFullYear(), date.getMonth() + 1, 7).toLocaleDateString('pt-br')
      return converterLocaleDateStringParaOFormatoIsoDateString(newDate)
    }

    const dataInicio = ref(getDataInicialMes())
    const dataFim = ref(getFinalMesSomada7Dias())

    // Função para buscar a lista de agentes (SÓ GESTOR USA)
    async function fetchAgentes() {
      try {
        const response = await ColaboradorService.get()
        listaAgentes.value = response.data || response
      } catch (e) {
        console.error(e)
      }
    }

    async function getAgendamentos() {
      if (isGetAgendamentosLoading.value) return

      try {
        isGetAgendamentosLoading.value = true

        // MUDANÇA 2: Passando o ID do agente selecionado (ou do próprio usuário) para a API
        // Certifique-se que seu TicketService.getAgenteAgendamentos aceita esse 3º parametro
        agendamentos.value = await TicketService.getAgenteAgendamentos(
          dataInicio.value,
          dataFim.value,
          agenteIdSelecionado.value, // <--- ID do Agente
        )

        events.value = agendamentos.value
          .map(agendamento => ({
            periodoDiaId: agendamento.agenteDisponibilidadePeriodoDiaId,
            name: mapPeriodoDiaParaTitulo(agendamento.agenteDisponibilidadePeriodoDiaId),
            start: agendamento.dataSolicitadaParaAgendamento,
            end: agendamento.dataFinalAgendamento,
            color: 'rgba(171, 211, 244, 0.9)',
            timed: true,
            ticketAgendamentoId: agendamento.id,
            ticketProtocolo: agendamento.ticketProtocolo,
            tipoAgendamentoNome: agendamento.tipoAgendamentoNome,
            solicitanteNome: agendamento.solicitanteNome,
            solicitanteEmail: agendamento.solicitanteEmail,
            solicitanteCelular: agendamento.solicitanteCelular,
            dataSolicitadaParaAgendamento: agendamento.dataSolicitadaParaAgendamento,
            dataFinalAgendamento: agendamento.dataFinalAgendamento,
            enderecoVisita: agendamento.enderecoVisita,
          }))
      } finally {
        isGetAgendamentosLoading.value = false
      }
    }

    async function handleFiltrarBtnClick() {
      await getAgendamentos()

      calendarKey.value++

      if (!agendamentos.value.length) {
        Vue.$toast.warning('Nenhum agendamento marcado para esse período.')
      }
    }

    onMounted(async () => {
      // MUDANÇA 3: Lógica Inicial
      if (isGestor.value) {
        // Gestor busca a lista para popular o select
        await fetchAgentes()
        // Opcional: Se quiser que já comece filtrando pelo próprio gestor, descomente abaixo:
        // agenteIdSelecionado.value = user.value.id
      } else {
        // Agente NÃO busca lista (evita 403) e trava o filtro nele mesmo
        agenteIdSelecionado.value = user.value.id
      }

      // Busca os agendamentos iniciais
      getAgendamentos()
    })

    return {
      agendamentos,
      events,
      isGetAgendamentosLoading,
      dataInicio,
      dataFim,
      calendarKey,
      handleFiltrarBtnClick,
      // Retornos novos
      listaAgentes,
      agenteIdSelecionado,
      handleSelecionarEquipe: handleFiltrarBtnClick, // Alias para o @change do select
      isGestor,
    }
  },
}
</script>
